// Scrolly-Mocks — Phase 11 Step 3.
// Quelle: Scrolly Platform.html (HomeContent).
// Verschachteltes Shape, das sich an den künftigen ScrollyCardDto annähert.
//
// Entscheidungen (Session C Q&A, 2026-04-22):
// - ≥16 Items, alle 11 Kategorien vertreten. `foryou`/`trending` sind Feed-Rails,
//   keine Scrolly-Kategorien — werden hier nicht als categoryId gesetzt.
// - `cover.palette` ist ein Palette-Key (nicht CSS-String) — die Cover-Komponente
//   löst das Token auf, nicht der Datenlayer.
// - `cover.decoration` ist frei wählbar (nicht kategorie-gekoppelt). Die Factory
//   nutzt ein Default-Mapping, lässt sich aber pro Item überschreiben.
// - `cover.bigNumberValue` nur gesetzt wenn `decoration === "bigNumber"`.
//   **Nicht** aus `stats.views` ableiten — es ist redaktionelle Headline.
// - `viewsLabel` wird aus `stats.views` über `lib/format.ts#formatViews` abgeleitet,
//   gehört nicht ins Schema.
// - `titleAccent?` optional — wenn gesetzt, markiert das Wort im Hero-Title kursiv+pink.
//   Sonst Fallback auf last-word.

import type { PostJson } from "@scrolly/post-types";
import type { CategoryId } from "../categories";
import { CREATORS_BY_HANDLE, type MockCreator } from "./creators";
import { mockPostJsonHamburgsMietpreise } from "./postJson/hamburgsMietpreise";

// ---- Palette & Decoration IDs ------------------------------------------

// Keys aus COVER_PALETTES (wird später in `lib/tokens.ts` exportiert).
// Wir listen sie hier als Union, damit die Mocks typisiert bleiben, ohne
// den Token-Block aus Step 3 Block 2 vorzugreifen.
export type PaletteKey =
  | "data"
  | "journalism"
  | "science"
  | "design"
  | "history"
  | "travel"
  | "business"
  | "howto"
  | "brand";

// 8 Core-Decorations + `stripes` (Brand-Fallback).
// Finale Namen aus handoff/phase-11/cover-variants.jsx.
export type DecorationId =
  | "chart"
  | "headlines"
  | "dots"
  | "phones"
  | "column"
  | "mountain"
  | "bigNumber"
  | "grid"
  | "stripes";

// Default-Mapping (Empfehlung, überschreibbar pro Item).
const DEFAULT_DECORATION: Record<CategoryId, DecorationId> = {
  all: "headlines",
  trending: "headlines",
  data: "chart",
  journalism: "headlines",
  science: "dots",
  design: "phones",
  history: "column",
  travel: "mountain",
  business: "bigNumber",
  howto: "grid",
  brand: "stripes",
  // Sprint-1-Fallback "story" (hidden category): neutrale Default-
  // Decoration für Backend-Scrollys ohne categoryName.
  story: "headlines",
};

// Default-Palette pro Kategorie (überschreibbar).
const DEFAULT_PALETTE: Record<CategoryId, PaletteKey> = {
  all: "journalism",
  trending: "journalism",
  data: "data",
  journalism: "journalism",
  science: "science",
  design: "design",
  history: "history",
  travel: "travel",
  business: "business",
  howto: "howto",
  brand: "brand",
  // Sprint-1-Fallback "story": neutrale journalism-Palette (analog "all"/
  // "trending"). Stimmt mit synthScrollyFromResolved.cover.palette überein.
  story: "journalism",
};

// ---- Schema ------------------------------------------------------------

export type ScrollyCover = {
  palette: PaletteKey;
  decoration: DecorationId;
  /** Nur gesetzt wenn decoration === "bigNumber". Redaktionelle Headline, nicht aus stats.views. */
  bigNumberValue?: string;
};

export type ScrollyStats = {
  /** Absolute Viewcount. `viewsLabel` kommt aus `lib/format.ts#formatViews(views)`. */
  views: number;
  /** Dauer-Label, z.B. "4 Min Scroll" (deutsch, aus Design-Quelle). */
  duration: string;
  likes: number;
  saves: number;
};

/** Denormalisierter Creator-Ausschnitt, eingebettet in jeden Scrolly. */
export type ScrollyCreatorRef = Pick<MockCreator, "handle" | "name" | "avatar">;

/**
 * Source-Herkunft des Scrolly-Contents — Phase 11.5, siehe
 * handoff/FEATURE-11.5-THIRD-PARTY-PREP.md.
 * - `native`   = im Scrolly-Studio gebaut, Renderer via.scrolly.com/:slug.
 *                Volle Branding-Pipeline.
 * - `embed`    = externer Publisher hostet selbst, Viewer iframed `externalUrl`.
 *                Keine Branding-Overrides.
 * - `imported` = HTML/Assets bei uns hochgeladen, wir hosten auf Sandbox-Subpfad.
 *                Eingeschränkte Branding-Integration.
 */
export type ScrollySource = "native" | "embed" | "imported";

export type MockScrolly = {
  id: string;
  slug: string;
  title: string;
  /** Optional: Wort im Hero-Titel, das kursiv+pink gerendert wird. Ohne Angabe: last-word-Fallback. */
  titleAccent?: string;
  description?: string;
  categoryId: CategoryId;
  creator: ScrollyCreatorRef;
  cover: ScrollyCover;
  /**
   * Reader-Cover (2026-06-01): echter Pro-Scrolly-Titelbild-URL aus dem
   * Scrolly-JSON (`metadata.thumbnailResized.url` u. a.), gespiegelt aus
   * Cover-Fix A. Wenn gesetzt, rendert `CoverSection` das Bild als START-
   * Hero-Hintergrund; sonst der konstante dunkle Fallback. Optional, weil
   * Feed-Mocks / Scrollys ohne Thumbnail keins haben. Lese-Logik:
   * `public/src/lib/cover-thumbnail.ts`.
   */
  coverImage?: string;
  stats: ScrollyStats;
  /** Relativ-deutsch für Phase 11, z.B. "vor 3 Tagen". */
  publishedAt: string;
  featured: boolean;
  tags: string[];
  /** Publisher-Quelle. Default `"native"`, siehe `ScrollySource`. */
  source: ScrollySource;
  /** Nur bei `source === "embed"`: absolute URL des externen Publishers. */
  externalUrl?: string;
  /** Nur bei `source === "imported"`: Publisher-Display-Name (z.B. "NZZ Visuals"). */
  importedFrom?: string;
  /** Optional: Lizenz-Kurz-Label, z.B. "CC-BY-4.0" oder "©Publisher". */
  licensedUnder?: string;
  /**
   * Phase 12 Step 2: optional PostJson payload used by the renderer
   * on /s/:slug. Present only on "native"/"imported" mocks that we
   * want to smoke-test through the WebGL pipeline — real content
   * will come from a backend fetch in a later phase.
   */
  postJson?: PostJson;
  /**
   * Plan-1.9 Phase 3.A.CP3: Backend-publicKey, falls aus
   * ResolvedScrolly synthetisiert. Wird als Resume-State-Storage-Key
   * verwendet (`scrolly:resume:{publicKey}`). Optional, weil Feed-Mocks
   * keinen Backend-publicKey haben — auf Mock-only-Pfad ist
   * Resume-Feature inaktiv.
   */
  publicKey?: string;
  /**
   * Plan-1.9 Phase 3.A.CP3: `scrolly.updatedAt` ISO-String, falls aus
   * ResolvedScrolly synthetisiert. Wird als `scrollyVersion` im Resume-
   * State persistiert (Master-Briefing v5 Z. 358) — Mismatch zeigt
   * "Inhalt wurde aktualisiert"-Variante (Z. 372). Optional analog
   * publicKey.
   */
  updatedAt?: string;
  /**
   * Block 2.6 P7 (2026-05-22): Legal-Links aus ResolvedScrolly. Wird vom
   * Colophon-Abbinder konsumiert für echte <a href>-Tags (Impressum,
   * Privacy, etc.). Optional, weil Mock-Pfade keine Backend-Org haben.
   */
  legalLinks?: {
    imprintUrl?: string;
    privacyUrl?: string;
    accessibilityUrl?: string;
    address?: string;
  };
  /**
   * Plan-N2 Block 2 (2026-05-25 Bene): Org-Kontakt aus ResolvedScrolly,
   * für den Colophon-Contact-Block. Quelle: Backend-Org-Settings
   * (`organization.name` + `organization.contactEmail`). Mirror der
   * app/-Stack-Pipeline (StoryFooter.tsx:117–137), die das gleiche Feld
   * konsumiert. Optional weil Mock-Pfade ohne Backend keinen Org-Kontakt
   * haben.
   */
  orgContact?: {
    name: string;
    email?: string;
  };
};

// ---- Factory -----------------------------------------------------------

type ScrollyInput = {
  id: string;
  slug: string;
  title: string;
  titleAccent?: string;
  description?: string;
  categoryId: CategoryId;
  creatorHandle: string;
  cover?: Partial<ScrollyCover>;
  stats: ScrollyStats;
  publishedAt: string;
  featured?: boolean;
  tags: string[];
  /** Phase 11.5: Source-Herkunft, Default `"native"`. */
  source?: ScrollySource;
  /** Phase 12 Step 2: optional PostJson for renderer-based /s/:slug smoke-tests. */
  postJson?: PostJson;
  externalUrl?: string;
  importedFrom?: string;
  licensedUnder?: string;
};

function createScrolly(input: ScrollyInput): MockScrolly {
  const creator = CREATORS_BY_HANDLE[input.creatorHandle];
  if (!creator) {
    throw new Error(
      `Mock-Scrolly ${input.id}: unbekannter creatorHandle "${input.creatorHandle}"`,
    );
  }

  const palette =
    input.cover?.palette ?? DEFAULT_PALETTE[input.categoryId];
  const decoration =
    input.cover?.decoration ?? DEFAULT_DECORATION[input.categoryId];
  const bigNumberValue =
    decoration === "bigNumber" ? input.cover?.bigNumberValue : undefined;
  const source = input.source ?? "native";

  // Schema-Invarianten (Phase 11.5) — explizite Fehlermeldungen beim Mock-Build,
  // damit Felder nicht schweigend ignoriert werden.
  if (source === "embed" && !input.externalUrl) {
    throw new Error(
      `Mock-Scrolly ${input.id}: source="embed" benötigt externalUrl.`,
    );
  }
  if (source === "imported" && !input.importedFrom) {
    throw new Error(
      `Mock-Scrolly ${input.id}: source="imported" benötigt importedFrom.`,
    );
  }

  return {
    id: input.id,
    slug: input.slug,
    title: input.title,
    titleAccent: input.titleAccent,
    description: input.description,
    categoryId: input.categoryId,
    creator: {
      handle: creator.handle,
      name: creator.name,
      avatar: creator.avatar,
    },
    cover: {
      palette,
      decoration,
      ...(bigNumberValue !== undefined ? { bigNumberValue } : {}),
    },
    stats: input.stats,
    publishedAt: input.publishedAt,
    featured: input.featured ?? false,
    tags: input.tags,
    source,
    ...(input.externalUrl !== undefined ? { externalUrl: input.externalUrl } : {}),
    ...(input.importedFrom !== undefined ? { importedFrom: input.importedFrom } : {}),
    ...(input.licensedUnder !== undefined ? { licensedUnder: input.licensedUnder } : {}),
    ...(input.postJson !== undefined ? { postJson: input.postJson } : {}),
  };
}

// ---- Daten -------------------------------------------------------------

// Shape:
// - thumbGradient → cover.palette (Key statt CSS-String)
// - viewsLabel entfällt (formatViews-Ableitung)
// - saveCount → stats.saves
// Plus 4 Items: journalism, science, design, data (Kategorie-Lücken auffüllen).

export const scrollys: MockScrolly[] = [
  // --- Ursprüngliche 12 ---------------------------------------------------
  createScrolly({
    id: "s1",
    slug: "achtmilliarden",
    title: "Wie die Weltbevölkerung auf 8 Milliarden wuchs",
    titleAccent: "Milliarden",
    description:
      "Eine scrollbare Datengeschichte über acht Jahrhunderte menschlichen Wachstums — von der Pest bis heute.",
    categoryId: "data",
    creatorHandle: "datastories",
    cover: { decoration: "bigNumber", bigNumberValue: "8Mrd" },
    stats: {
      views: 2_400_000,
      duration: "4 Min Scroll",
      likes: 48_000,
      saves: 12_000,
    },
    publishedAt: "vor 3 Tagen",
    featured: true,
    tags: ["Daten", "Geschichte", "Wissenschaft"],
    // Phase 12 Step 2: attaches the smoke-test PostJson (from dev-mietpreise
    // DB row) so /s/achtmilliarden drives the real WebGL renderer. The
    // cover/stats/title stay fictional — only the slide payload is real.
    postJson: mockPostJsonHamburgsMietpreise,
  }),
  createScrolly({
    id: "s2",
    slug: "mobile-first",
    title: "Mobile First: 14 Apps, die das Telefon neu definiert haben",
    description:
      "Eine visuelle Geschichte des Mobile-Interaktionsdesigns — jede Geste, jeder Pixel, der verändert hat, wie wir die Welt halten.",
    categoryId: "design",
    creatorHandle: "framerweekly",
    stats: {
      views: 890_000,
      duration: "6 Min Scroll",
      likes: 22_000,
      saves: 8_100,
    },
    publishedAt: "vor 1 Woche",
    tags: ["Design", "Tech"],
  }),
  createScrolly({
    id: "s3",
    slug: "gletscher-3d",
    title: "Im Inneren eines Gletschers: Ein 3D-Abstieg",
    titleAccent: "3D-Abstieg",
    description:
      "Scroll 300 Meter in einen alpinen Gletscher. Blaues Eis, Schmelzwasserflüsse und die stille Architektur des Winters.",
    categoryId: "science",
    creatorHandle: "atlas",
    stats: {
      views: 1_100_000,
      duration: "5 Min Scroll",
      likes: 34_000,
      saves: 19_000,
    },
    publishedAt: "vor 2 Tagen",
    tags: ["Wissenschaft", "Natur", "3D"],
  }),
  createScrolly({
    id: "s4",
    slug: "matterhorn-hoehe",
    title: "Das Matterhorn, erzählt durch die Höhe",
    categoryId: "travel",
    creatorHandle: "atlas",
    stats: {
      views: 620_000,
      duration: "3 Min Scroll",
      likes: 18_000,
      saves: 6_200,
    },
    publishedAt: "vor 5 Tagen",
    tags: ["Reise", "Geografie"],
  }),
  // s5 — Embed von Bloomberg Graphics (Phase 11.5).
  // Business/BigNumber-Optik passt zu einem Markets-Explainer; externer Content
  // setzt eigenes Styling, unser Cover bleibt Scrolly-Native-Look.
  createScrolly({
    id: "s5",
    slug: "apple-stock-split",
    title: "Warum Apples Aktiensplit dem Retail nie geschadet hat",
    categoryId: "business",
    creatorHandle: "bloomberg",
    cover: { bigNumberValue: "$340B" },
    stats: {
      views: 340_000,
      duration: "7 Min Scroll",
      likes: 9_400,
      saves: 4_100,
    },
    publishedAt: "vor 6 Stunden",
    tags: ["Business", "Finanzen"],
    source: "embed",
    externalUrl:
      "https://www.bloomberg.com/graphics/2026-apple-stock-split-retail/",
  }),
  // s6 — Imported von NZZ Visuals (Phase 11.5).
  // Bleibt beim Native-Creator "codex" — Imports werden redaktionell einem
  // Scrolly-Account zugeordnet; der Ursprung wird über `importedFrom` transportiert.
  createScrolly({
    id: "s6",
    slug: "roemische-aquaedukte",
    title: "Ein visueller Führer durch die römischen Aquädukte",
    categoryId: "history",
    creatorHandle: "codex",
    stats: {
      views: 710_000,
      duration: "8 Min Scroll",
      likes: 21_000,
      saves: 13_000,
    },
    publishedAt: "vor 2 Wochen",
    tags: ["Geschichte", "Architektur"],
    source: "imported",
    importedFrom: "NZZ Visuals",
    licensedUnder: "CC-BY-4.0",
  }),
  createScrolly({
    id: "s7",
    slug: "pitch-deck-2m",
    title: "Wie ich 2M € mit einem scrollbaren Pitch-Deck eingesammelt habe",
    titleAccent: "Pitch-Deck",
    categoryId: "business",
    creatorHandle: "framerweekly",
    cover: { bigNumberValue: "2M€" },
    stats: {
      views: 1_800_000,
      duration: "5 Min Scroll",
      likes: 52_000,
      saves: 28_000,
    },
    publishedAt: "vor 4 Tagen",
    featured: true,
    tags: ["Business", "Design"],
  }),
  createScrolly({
    id: "s8",
    slug: "marathon-zielstrich",
    title: "Die Anatomie einer Marathon-Ziellinie",
    titleAccent: "Ziellinie",
    categoryId: "journalism",
    creatorHandle: "framerweekly",
    stats: {
      views: 220_000,
      duration: "4 Min Scroll",
      likes: 7_800,
      saves: 2_900,
    },
    publishedAt: "vor 1 Tag",
    tags: ["Sport"],
  }),
  createScrolly({
    id: "s9",
    slug: "spacex-launches",
    title: "Jeder SpaceX-Start, animiert",
    titleAccent: "animiert",
    categoryId: "science",
    creatorHandle: "orbital",
    stats: {
      views: 3_200_000,
      duration: "9 Min Scroll",
      likes: 89_000,
      saves: 41_000,
    },
    publishedAt: "vor 3 Wochen",
    featured: true,
    tags: ["Wissenschaft", "Raumfahrt"],
  }),
  createScrolly({
    id: "s10",
    slug: "sauerteig-rezept",
    title: "Sauerteig, aber ein scrollbares Rezept",
    titleAccent: "Rezept",
    categoryId: "howto",
    creatorHandle: "datastories",
    stats: {
      views: 420_000,
      duration: "6 Min Scroll",
      likes: 14_000,
      saves: 22_000,
    },
    publishedAt: "vor 1 Woche",
    tags: ["Anleitungen", "Kochen"],
  }),
  createScrolly({
    id: "s11",
    slug: "ikea-katalog",
    title: "Der IKEA-Katalog, neu gedacht",
    categoryId: "brand",
    creatorHandle: "framerweekly",
    stats: {
      views: 580_000,
      duration: "4 Min Scroll",
      likes: 16_000,
      saves: 9_800,
    },
    publishedAt: "vor 5 Tagen",
    tags: ["Brand", "Design"],
  }),
  createScrolly({
    id: "s12",
    slug: "zinsstruktur-inversion",
    title: "Warum sich die Zinsstrukturkurve gerade umgekehrt hat, in Charts",
    titleAccent: "Charts",
    categoryId: "business",
    creatorHandle: "theledger",
    // Business ohne BigNumber — Chart-Story — überschreibt Default.
    cover: { decoration: "chart" },
    stats: {
      views: 190_000,
      duration: "5 Min Scroll",
      likes: 4_200,
      saves: 1_900,
    },
    publishedAt: "vor 12 Stunden",
    tags: ["Business", "Finanzen"],
  }),

  // --- 4 neue Items: Kategorie-Lücken & Dekorations-Vielfalt --------------
  // s13 — Embed von SZ Interaktiv (Phase 11.5).
  // Wahlgeografie ist das Kern-Sujet von SZ-Interaktiv; plausible Reframing
  // des Titels, Slug bleibt narrativ.
  createScrolly({
    id: "s13",
    slug: "wahlkreis-karte",
    title: "Wie sich die Wahlkreise seit 1990 verschoben haben",
    titleAccent: "verschoben",
    description:
      "Fünf Jahrzehnte deutsche Wahlgeografie, Bundesland für Bundesland.",
    categoryId: "journalism",
    creatorHandle: "sz-interaktiv",
    stats: {
      views: 145_000,
      duration: "7 Min Scroll",
      likes: 5_200,
      saves: 2_400,
    },
    publishedAt: "vor 2 Tagen",
    tags: ["Journalismus", "Politik", "Daten"],
    source: "embed",
    externalUrl:
      "https://www.sueddeutsche.de/projekte/artikel/politik/wahlkreise-1990-2026/",
  }),
  createScrolly({
    id: "s14",
    slug: "mondfinsternis-anatomie",
    title: "Die Anatomie einer totalen Mondfinsternis",
    titleAccent: "Mondfinsternis",
    description:
      "Sieben Phasen, eine Stunde Schatten — visuell erklärt.",
    categoryId: "science",
    creatorHandle: "orbital",
    // Science meist dots — hier Chart für Orbital-Mechanik-Optik.
    cover: { decoration: "chart" },
    stats: {
      views: 780_000,
      duration: "5 Min Scroll",
      likes: 24_000,
      saves: 11_000,
    },
    publishedAt: "vor 6 Tagen",
    tags: ["Wissenschaft", "Astronomie"],
  }),
  createScrolly({
    id: "s15",
    slug: "bauhaus-grid",
    title: "Das Bauhaus-Raster: Warum es bis heute Produkte formt",
    titleAccent: "Raster",
    description:
      "Von Dessau zur App-Icon-Grid — ein visuelles Essay.",
    categoryId: "design",
    creatorHandle: "studio-kranz",
    // Design meist phones — hier grid für Typo-Raster-Thema.
    cover: { decoration: "grid" },
    stats: {
      views: 360_000,
      duration: "6 Min Scroll",
      likes: 11_000,
      saves: 6_800,
    },
    publishedAt: "vor 9 Tagen",
    tags: ["Design", "Typografie"],
  }),
  // s16 — Embed von The Pudding (Phase 11.5).
  // Data-Story mit Chart-Decoration ist das Pudding-typische Sujet; Titel
  // englisch-deutsch gemischt, damit die Herkunft im Feed sichtbar bleibt.
  createScrolly({
    id: "s16",
    slug: "atmung-der-erde",
    title: "Die Atmung der Erde — 400 Jahre CO₂ in einer Kurve",
    titleAccent: "Atmung",
    description:
      "Vom Eiskern bis Mauna Loa — die längste Klimakurve der Welt.",
    categoryId: "data",
    creatorHandle: "pudding",
    // Data — explizit chart statt bigNumber (vier Jahrhunderte Kurve).
    cover: { decoration: "chart" },
    stats: {
      views: 2_050_000,
      duration: "8 Min Scroll",
      likes: 61_000,
      saves: 34_000,
    },
    publishedAt: "vor 1 Woche",
    featured: true,
    tags: ["Daten", "Klima", "Wissenschaft"],
    source: "embed",
    externalUrl: "https://pudding.cool/2026/03/atmung-der-erde/",
  }),
];

// ---- Convenience-Lookups -----------------------------------------------

export const SCROLLYS_BY_ID = Object.fromEntries(
  scrollys.map((s) => [s.id, s]),
) as Record<string, MockScrolly>;

export const SCROLLYS_BY_SLUG = Object.fromEntries(
  scrollys.map((s) => [s.slug, s]),
) as Record<string, MockScrolly>;
